import React, { useRef, useState, useMemo, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Container from '@material-ui/core/Container'
import Hidden from '@material-ui/core/Hidden'
import Typography from '@material-ui/core/Typography'
import FormHelperText from '@material-ui/core/FormHelperText'
import CustomCardForm from './CustomCardForm'
import CardBuilderModal from './CardBuilderModal'
import {
    CARD_HEIGHT_DEFAULT,
    CARD_WIDTH_DEFAULT,
    CARD_TYPE,
    CARD_STYLE,
    DEFAULT_LOAD_VALUE,
    CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_VISA,
    CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_MC,
    DELIVERY_METHOD_OPTIONS_TWO_LINE,
} from '../../../constants'
import { createACardRootStyles } from '../themeStyles'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import { useSelector } from 'react-redux'
import {
    MASTERCARD_OVERLAY_ID,
    VISA_OVERLAY_ID,
} from '../../../containers/CreateACard'
import { getCardNetwork } from '../../../utils/getCardNetwork'
import { pushBuildACardProductViewToDataLayer } from '../../../utils/dataLayer'
import dataURItoBlob from '../../../utils/dataURItoBlob'
import { useQueryParam } from '../../../hooks/useQueryParam'
import GreetingCardVideoModal from './GreetingCardVideoModal'
import useUploadImageToCloudinary from '../../../hooks/useUploadImageToCloudinary'
import { useLocation } from 'react-router-dom'
import {
    getCloudinaryPublicIdFromUrl,
    getCloudinaryPublicIdFromWcpProductOrGiftTemplate,
    isCloudinaryUrl,
} from '../../../utils/cloudinaryUtils'
import BacCardPreview from '../../Base/CardPreview/BacCardPreview'
import CardTypeSelector from '../../Base/Selectors/CardTypeSelector'
import useFetchOpenLoopProducts from '../../../hooks/useFetchOpenLoopProducts'
import useFetchGiftTemplates from '../../../hooks/useFetchGiftTemplates'
import { Transformation } from 'cloudinary-react'
import BacCardDesignSelector from './BacCardDesignSelector/BacCardDesignSelector'
import ShippingInfoText from './ShippingInfoText'
import Alert from '@material-ui/lab/Alert'
import useFetchImageRulesResult from '../../../hooks/useFetchImageRulesResult'
import { trackAiImageCheckResult } from '../../../utils/aiImageCheckTracking'
import {
    hasImageRulesViolations,
    isImageRulesResultBlocked,
} from '../../../utils/imageRulesResult'
import DesignSelectionModal from '../../Modals/DesignSelectionModal'
import CardDetailsFromUrl from '../../GreetingCard/CardDetailsFromUrl'
import Headline from './Headline'

const bacUrlSegment = '/buildacard/'

const useStyles = makeStyles((theme) => ({
    ...createACardRootStyles,
    // Reserve the design picker's footprint while designs load so the
    // content below it doesn't shift (CLS) when the real grid renders.
    // Mirrors the grid in BacCardDesignSelector.
    loadingCardDesignsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: '0.5rem',
        rowGap: '0.5rem',
        maxWidth: 350,
        justifyItems: 'center',
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
            columnGap: '1rem',
            maxWidth: '600px',
        },
    },
    loadingCardDesignsItem: {
        width: '100%',
        maxWidth: '180px',
        aspectRatio: '180 / 114',
        borderRadius: '4px',
        backgroundColor: '#ececec',
    },
    errorLoadingDesignsAlert: {
        fontSize: '1rem',
        margin: '1rem 0',
    },
    root: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        minWidth: '100%',

        '& label.Mui-focused': {
            color: theme.palette.grey.darkestGrey,
        },
        '& label.MuiFormLabel-filled': {
            '&.Mui-error': {
                color: `${theme.palette.error.main}`,
            },
            color: theme.palette.primary.main,
            '& ~ .MuiOutlinedInput-root.Mui-error fieldset': {
                borderColor: `${theme.palette.error.main}`,
            },
            '& ~ .MuiOutlinedInput-root fieldset': {
                borderColor: `${theme.palette.primary.main}`,
            },
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: '#4F4F4F',
                borderRadius: '8px',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.grey.darkestGrey,
            },
        },
    },
    cardWrapper: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px!important',
        paddingBottom: '0!important',
        [theme.breakpoints.up('md')]: {
            justifyContent: 'flex-start',
            paddingTop: '0!important',
        },
    },
    customCardFormWrapper: {
        padding: '0 !important',
        [theme.breakpoints.up('md')]: {
            marginTop: 0,
        },
    },
    customCardForm: {
        margin: '0 auto',
        padding: '0 24px',
        [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
        },
        [theme.breakpoints.up('md')]: {
            marginTop: '2rem',
        },
    },
    topContainer: {
        marginTop: '1.25rem',
        backgroundColor: '#FFFFFF',
        [theme.breakpoints.up('md')]: {
            padding: '0 2rem 2rem 2rem',
        },
    },
    containerContent: {
        backgroundColor: 'white',
        borderRadius: '5px',
        [theme.breakpoints.up('md')]: {
            paddingTop: '1.5rem',
        },
    },
    h1: {
        color: '#202020',
        fontSize: '36px',
        marginTop: '24px',
        lineHeight: 'normal',
        letterSpacing: '-1px',
        [theme.breakpoints.up('md')]: {
            fontSize: '40px',
            marginBottom: '24px',
            marginTop: '0',
        },
    },
    pageContentContainer: {
        maxWidth: '100%',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            maxWidth: '100%',
        },
    },
    cardIndexText: {
        textAlign: 'start',
        fontWeight: 'bold',
        color: '#6E1A6A',
        fontSize: '15px',
    },
    bacSelectorContainer: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            padding: '1rem 2rem 0px 0px',
            maxWidth: 500,
        },
        [theme.breakpoints.up(450)]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {},
    },
    registeredTrademark: {
        fontSize: '1.3rem',
        bottom: '10px',
        position: 'relative',
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.7rem',
        fontWeight: 'bold',
        textAlign: 'center',
        marginTop: '0.5rem',
        marginBottom: '.5rem',
        [theme.breakpoints.up('md')]: {
            marginBottom: 0,
            fontSize: '1rem',
        },
    },
    sectionHeaders: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#202020',
        letterSpacing: '-0.5px',
        paddingBottom: '8px',
        paddingTop: '24px',
    },
    subheader: {
        fontSize: '16px',
        color: '#202020',
        paddingBottom: '8px',
    },
    backButtonWrapper: {
        marginBottom: '6px',
        [theme.breakpoints.up('md')]: {
            marginBottom: '20px',
        },
    },
}))

const CreateACardLanding = () => {
    const location = useLocation()
    const query = useQueryParam()
    const path = location.pathname
    const cardNetworkParam = query.get('network')
        ? query.get('network')
        : path.includes('mastercard')
        ? 'mastercard'
        : 'visa'
    const cardTypeQueryParam = query.get('type')
    const quantityQueryParam = query.get('quantity')

    //Single state object for all URL parameters
    const [urlData, setUrlData] = useState({
        slug: '',
        cloudinary_id: '',
        carrier_type: '',
        message: '',
    })
    // Get query parameters once on component mount
    useEffect(() => {
        const query = new URLSearchParams(window.location.search)

        // Set all values at once with a single state update
        setUrlData({
            message: query.get('message') || '',
            slug: query.get('design_id') || '',
            carrier_type: query.get('carrier_type') || '',
            cloudinary_id: query.get('image') || '',
        })
    }, [])
    const canShowGreetingCardContent =
        urlData.message &&
        urlData.slug &&
        urlData.carrier_type &&
        urlData.cloudinary_id

    const imageInputRef = useRef(null)
    const predesignsListRef = useRef(null)
    const cardPreviewRef = useRef(null)

    const {
        availableCardNetworks,
        defaultCardNetwork,
        defaultDeliveryMethod,
        baseConfig,
        baseUrl,
        imageGuidelinesUrl,
        activeCard,
        customCards,
        isEditing,
    } = useSelector((state) => state.createACard)
    const classes = useStyles(baseConfig)
    const {
        mutate: uploadImageToCloudinary,
        reset: resetUploadImageToCloudinaryMutation,
        isSuccess: successfullyUploadedImageToCloudinary,
        isError: errorUploadingImageToCloudinary,
        isLoading: uploadingImageToCloudinary,
    } = useUploadImageToCloudinary()
    const {
        mutate: fetchImageRulesResult,
        data: imageRulesResult,
        isLoading: imageRulesResultIsLoading,
        isError: imageRulesResultError,
    } = useFetchImageRulesResult()

    const [imageError, setImageError] = useState('')
    const [cropper, setCropper] = useState(null)
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [cloudinaryId, setCloudinaryId] = useState(activeCard?.cloudinaryId) // for user uploaded image
    const [imageRulesCheckResult, setImageRulesCheckResult] = useState({})
    const [selectedCardDesign, setSelectedCardDesign] = useState(
        activeCard?.design
    ) // for plastic/virtual predesigns
    const [imageFile, setImageFile] = useState('')
    const [cardNetwork, setCardNetwork] = useState(
        activeCard?.cardNetwork ?? defaultCardNetwork
    )
    const [deliveryMethod, setDeliveryMethod] = useState(
        activeCard?.deliveryMethod ?? defaultDeliveryMethod
    )
    const [cardStyle, setCardStyle] = useState('')

    const [quantity, setQuantity] = useState(
        !isEmpty(customCards) ? customCards.length : 1
    )
    const [nameOnCard, setNameOnCard] = useState(activeCard?.nameOnCard ?? '')
    const [messageOnCard, setMessageOnCard] = useState(
        activeCard?.messageOnCard ?? ''
    )
    const [amount, setAmount] = useState(
        activeCard?.amount ?? DEFAULT_LOAD_VALUE
    )
    const [applyCardImageToAll, setApplyCardImageToAll] = useState(!isEditing)

    const [modalWithVideoIsOpen, setModalWithVideoIsOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const handleShowAllDesigns = () => {
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const cardOverlayId = useMemo(() => {
        return !isEmpty(cardNetwork) && cardNetwork.name === 'Visa'
            ? VISA_OVERLAY_ID
            : MASTERCARD_OVERLAY_ID
    }, [cardNetwork])

    const placeholderCardImageUrl = useMemo(() => {
        return !isEmpty(cardNetwork) && cardNetwork.name === 'Visa'
            ? baseConfig.placeholder_card_image_url
            : baseConfig.placeholder_card_image_url_mc
    }, [
        cardNetwork,
        baseConfig.placeholder_card_image_url,
        baseConfig.placeholder_card_image_url_mc,
    ])

    const previewImage = useMemo(() => {
        if (!isEmpty(cloudinaryId)) {
            return cloudinaryId
        }
        return null
    }, [cloudinaryId])

    const hasAnyImageViolations = (data) =>
        hasImageRulesViolations(data ?? imageRulesCheckResult)

    const isImageBlocked = (data) =>
        isImageRulesResultBlocked(data ?? imageRulesCheckResult)

    const isVirtual = useMemo(() => {
        return deliveryMethod ?? defaultDeliveryMethod
            ? deliveryMethod === CARD_TYPE.VIRTUAL
            : false
    }, [deliveryMethod, defaultDeliveryMethod])

    /**
     * previewImage - the cloudinary ID of an uploaded custom image
     * selectedCardDesign - if plastic, the open loop product selected, otherwise the gift template selected
     */
    const getCardPreviewCloudinaryPublicId = () => {
        let cloudinaryPublicId = previewImage

        // Predesign selected
        if (
            !isEmpty(selectedCardDesign) &&
            cardStyle === CARD_STYLE.PREDESIGNED
        ) {
            cloudinaryPublicId =
                getCloudinaryPublicIdFromWcpProductOrGiftTemplate(
                    selectedCardDesign,
                    true
                )
        }

        // 'Upload Photo' selected, no custom image uploaded yet
        if (!cloudinaryPublicId) {
            if (isVirtual) {
                // 'Digital' selected
                cloudinaryPublicId = cardNetwork.virtualImage
            } else {
                // 'Plastic' selected
                cloudinaryPublicId = isCloudinaryUrl(placeholderCardImageUrl)
                    ? getCloudinaryPublicIdFromUrl(placeholderCardImageUrl)
                    : placeholderCardImageUrl
            }
        }

        return cloudinaryPublicId
    }

    /**
     * Used to get the index of the current active card
     * @type {number|*}
     */
    const activeCardIndex = useMemo(() => {
        return isNull(activeCard)
            ? 1
            : customCards.findIndex(
                  (card) => card.cardId === activeCard.cardId
              ) + 1
    }, [activeCard, customCards])

    /**
     * If the activeCard is the last in the customCards array or if customCards
     * is empty and quantity is 1, we're on the last card.
     * @type {boolean}
     */
    const isLastCustomCard = useMemo(() => {
        if (customCards.length > 0) {
            return activeCardIndex === customCards.length
        } else {
            return quantity === 1
        }
    }, [activeCardIndex, customCards, quantity])

    /**
     * Don't show the apply all checkbox if we're editing a card or building
     * the last card, since apply all wouldn't do anything and could be confusing.
     * @type {boolean}
     */
    const showApplyAllCheckbox = useMemo(() => {
        return !isEditing && !isLastCustomCard
    }, [isEditing, isLastCustomCard])

    /**
     * Card index text to display in CardBuilderModal
     * @type {unknown}
     */
    const activeCardIndexText = useMemo(() => {
        if (customCards.length > 0) {
            return `Card ${activeCardIndex} of ${customCards.length}`
        } else {
            return `Card ${activeCardIndex} of ${quantity}`
        }
    }, [customCards, activeCardIndex, quantity])

    const showActiveCardIndexText = useMemo(() => {
        const cardCount = !isEmpty(customCards) ? customCards.length : quantity
        return !isEditing && cardCount > 1
    }, [isEditing, customCards, quantity])

    /**
     * Set card network from query param or location path if it exists and is valid
     */
    useEffect(() => {
        if (!isEmpty(cardNetworkParam)) {
            const cardNetwork = getCardNetwork(
                availableCardNetworks,
                cardNetworkParam
            )
            if (!isEmpty(cardNetwork)) {
                setCardNetwork(cardNetwork)
                pushBuildACardProductViewToDataLayer(cardNetwork.name)
            }
        }
    }, [cardNetworkParam, availableCardNetworks, setCardNetwork])

    /**
     * Set card type from query param if it exists and is valid
     */
    useEffect(() => {
        if (!isEmpty(cardTypeQueryParam)) {
            if (
                cardTypeQueryParam === CARD_TYPE.PLASTIC ||
                cardTypeQueryParam === CARD_TYPE.VIRTUAL
            ) {
                setDeliveryMethod(cardTypeQueryParam)
            }
        }
    }, [cardTypeQueryParam])

    /**
     * Set quantity from query param if it exists and is valid
     */
    useEffect(() => {
        const validateAndSetQuantity = (quantity) => {
            const parsedQuantity = parseInt(quantity)

            if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
                setQuantity(parsedQuantity)
            }
        }

        if (!isEmpty(quantityQueryParam)) {
            validateAndSetQuantity(quantityQueryParam)
        }
    }, [quantityQueryParam, setQuantity])

    const handleCloudinaryUpload = async (imageData) => {
        const formData = new FormData()
        formData.append('file', dataURItoBlob(imageData))
        // replace this with your upload preset name
        formData.append('upload_preset', 'pfpkv8cl')

        if (
            successfullyUploadedImageToCloudinary ||
            errorUploadingImageToCloudinary
        ) {
            resetUploadImageToCloudinaryMutation()
        }

        if (!uploadingImageToCloudinary) {
            uploadImageToCloudinary(formData, {
                onSuccess: (data) => {
                    if (!data) {
                        setImageError(
                            'Image upload failed. Please try again or select a new image'
                        )
                        return
                    }

                    setCloudinaryId(data.public_id)
                    handleCheckImageRules(data.url, data.public_id)
                },
                onError: (error) => {
                    setImageError(
                        'Image upload failed. Please try again or select a new image'
                    )
                },
            })
        }
    }

    const handleCheckImageRules = async (imageUrl, uploadedCloudinaryId) => {
        if (imageRulesResultIsLoading || uploadingImageToCloudinary) {
            return
        }

        const medium = isVirtual ? CARD_TYPE.VIRTUAL : CARD_TYPE.PLASTIC
        fetchImageRulesResult({
            imageUrl,
            cloudinaryId: uploadedCloudinaryId,
            cardNetwork: cardNetwork?.name?.toLowerCase(),
            medium,
        }, {
            onSuccess: (data) => {
                trackAiImageCheckResult(data, { medium })
                if (!data) {
                    setModalIsOpen(false)
                    return
                }
                setImageRulesCheckResult(data)
                if (isImageBlocked(data)) {
                    setCloudinaryId('')
                    return
                }
                if (!hasAnyImageViolations(data)) {
                    setModalIsOpen(false)
                }
            },
            onError: () => {
                setModalIsOpen(false)
            },
        })
    }

    const onSaveDesignAssets = async () => {
        if (isImageBlocked()) {
            return
        }

        if (hasAnyImageViolations()) {
            setModalIsOpen(false)
            return
        }

        const data = cropper
            .getCroppedCanvas({
                width: CARD_WIDTH_DEFAULT,
                height: CARD_HEIGHT_DEFAULT,
            })
            .toDataURL()
        await handleCloudinaryUpload(data).then(() => {
            setSelectedCardDesign('')
            setCardStyle(CARD_STYLE.UPLOAD_IMAGE)
            // Scroll to card preview after upload
            if (cardPreviewRef.current) {
                cardPreviewRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }
        })
    }

    const handleImageChange = (event, callback) => {
        setImageError('')
        setImageRulesCheckResult({})
        // Read the file and then upload in the background
        if (event.target.files.length > 0) {
            const reader = new FileReader()
            reader.onload = (event) => setImageFile(event.target.result)
            reader.readAsDataURL(event.target.files[0])
            if (callback && typeof callback === 'function') {
                callback()
            }
        }
    }

    const handleImageClick = () => {
        if (cardStyle === CARD_STYLE.PREDESIGNED) {
            predesignsListRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        } else {
            setImageRulesCheckResult({})
            setModalIsOpen(true)
        }
    }

    const bacUrlSegmentIndex = path?.indexOf(bacUrlSegment)

    const hasCustomSlug = () => {
        return (
            bacUrlSegmentIndex !== -1 &&
            bacUrlSegmentIndex + bacUrlSegment?.length < path?.length
        )
    }

    const hasSelectedPhoto = !isEmpty(cloudinaryId)
    const imageErrorRef = useRef(null)

    // Predesign-specific logic
    const {
        data: openLoopProducts,
        isLoading: loadingOpenLoopProducts,
        isError: errorFetchingOpenLoopProducts,
    } = useFetchOpenLoopProducts({
        includePlastic: 1,
        includeVirtual: 1,
        cardNetwork: cardNetwork?.name?.toLowerCase(),
    })
    const {
        data: giftTemplates,
        isLoading: loadingGiftTemplates,
        isError: errorFetchingGiftTemplates,
    } = useFetchGiftTemplates(1, 100, 0, true)
    const cloudinaryIdProperty = useMemo(() => {
        return !isVirtual ? 'cloudinary_id' : 'cloudinaryId'
    }, [isVirtual])
    const imageUrlProperty = useMemo(() => {
        return !isVirtual ? 'raw_card_image_url' : 'imageUrl'
    }, [isVirtual])
    const designNameProperty = useMemo(() => {
        return isVirtual ? 'name' : 'imageTitle'
    }, [isVirtual])
    const virtualOpenLoopProduct = openLoopProducts?.results?.find(
        (p) => p.virtual_card
    ) // save this with design if isVirtual for createACardAddToCart to use
    const designItemsCollection = useMemo(() => {
        if (!isVirtual) {
            let allOpenLoopPlasticProducts = openLoopProducts?.results?.filter(
                (p) => !p.virtual_card
            ) // exclude virtual WcpProduct we send as open_loop_product_id on add to cart & selectedCardDesign

            return allOpenLoopPlasticProducts
        } else {
            const allGiftTemplates = giftTemplates?.templates
            return allGiftTemplates
        }
    }, [isVirtual, openLoopProducts, giftTemplates])
    const overlayTransformationName =
        !isEmpty(cardNetwork) && cardNetwork.name === 'Visa'
            ? CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_VISA
            : CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_MC
    const embossOverlay = useMemo(() => {
        if (isVirtual) {
            return null
        }
        return <Transformation rawTransformation={overlayTransformationName} />
    }, [isVirtual, overlayTransformationName])

    const onDesignSelect = (design) => {
        setCardStyle(CARD_STYLE.PREDESIGNED)
        setImageError('')
        setCloudinaryId(null) // Clear any previously uploaded image so that if a user changes their mind and selects a predesign, the custom image is not used
        const selectedDesign = {
            ...design,
            // Need these values for add to cart
            open_loop_product_id: isVirtual
                ? virtualOpenLoopProduct?.id
                : design.id,
            gift_template_id: isVirtual ? design.id : undefined,
        }
        setSelectedCardDesign(selectedDesign)
        // Scroll to card preview after design select
        if (cardPreviewRef.current) {
            cardPreviewRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }
    }

    return (
        <div className={classes.root}>
            <GreetingCardVideoModal
                modalIsOpen={modalWithVideoIsOpen}
                closeModal={() => {
                    setModalWithVideoIsOpen(false)
                }}
            />
            <CardBuilderModal
                modalIsOpen={modalIsOpen}
                closeModal={() => {
                    setModalIsOpen(false)
                }}
                imageFile={imageFile}
                zoomInBackground={() => cropper.zoom(0.1)}
                zoomOutBackground={() => cropper.zoom(-0.1)}
                rotateLeft={() => cropper.rotate(-90)}
                rotateRight={() => cropper.rotate(90)}
                changeBackground={(e) => {
                    handleImageChange(e)
                }}
                saveDesignAssets={onSaveDesignAssets}
                imageGuidelinesUrl={imageGuidelinesUrl}
                hasAnyImageViolations={hasAnyImageViolations}
                isImageBlocked={isImageBlocked}
                imageRulesCheckResult={imageRulesCheckResult}
                openImageInput={() => {
                    imageInputRef.current.click()
                }}
                setCropper={setCropper}
                baseUrl={baseUrl}
                applyCardImageToAll={applyCardImageToAll}
                setApplyCardImageToAll={setApplyCardImageToAll}
                showApplyAllCheckbox={showApplyAllCheckbox}
                activeCardIndexText={activeCardIndexText}
                showActiveCardIndexText={!isEditing}
                uploadingImageToCloudinary={uploadingImageToCloudinary}
                imageRulesResultIsLoading={imageRulesResultIsLoading}
            />
            <Container className={classes.pageContentContainer}>
                <Container maxWidth="lg" className={classes.topContainer}>
                    <Grid
                        container
                        className={classes.containerContent}
                        spacing={4}
                    >
                        <Grid
                            item
                            xs={12}
                            md={6}
                            className={classes.cardWrapper}
                        >
                            {canShowGreetingCardContent && !isVirtual && (
                                <>
                                    <Headline
                                        hasCustomSlug={hasCustomSlug()}
                                        baseConfig={baseConfig}
                                        isVirtual={isVirtual}
                                        cardNetwork={cardNetwork}
                                        classes={classes}
                                        baseUrl={baseUrl}
                                    />
                                    <CardDetailsFromUrl
                                        urlData={urlData}
                                        nameOnCard={nameOnCard}
                                        messageOnCard={messageOnCard}
                                        cloudinaryId={cloudinaryId}
                                        amount={amount}
                                        cardNetworkName={cardNetwork}
                                        cloudinaryPublicId={getCardPreviewCloudinaryPublicId()}
                                    />
                                </>
                            )}
                            {(!canShowGreetingCardContent ||
                                (canShowGreetingCardContent && isVirtual)) && (
                                <div
                                    className={classes.bacSelectorContainer}
                                    ref={cardPreviewRef}
                                >
                                    <Headline
                                        hasCustomSlug={hasCustomSlug()}
                                        baseConfig={baseConfig}
                                        isVirtual={isVirtual}
                                        cardNetwork={cardNetwork}
                                        classes={classes}
                                        baseUrl={baseUrl}
                                    />
                                    <>
                                        <BacCardPreview
                                            cardNetwork={cardNetwork.name}
                                            isVirtual={isVirtual}
                                            cloudinaryPublicId={getCardPreviewCloudinaryPublicId()}
                                            showInnerBorder={!previewImage}
                                            showDynamicEmboss
                                            nameOnCard={nameOnCard}
                                            messageOnCard={messageOnCard}
                                            onClick={handleImageClick}
                                        />
                                        {deliveryMethod ===
                                            CARD_TYPE.PLASTIC && (
                                            <Hidden smDown>
                                                <ShippingInfoText />
                                            </Hidden>
                                        )}
                                    </>
                                </div>
                            )}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={6}
                            className={classes.customCardFormWrapper}
                        >
                            <div className={classes.customCardForm}>
                                {showActiveCardIndexText && (
                                    <Grid item xs={12}>
                                        <Typography
                                            data-testid="landing-card-index-text"
                                            className={classes.cardIndexText}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="11"
                                                height="12"
                                                viewBox="0 0 11 12"
                                                fill="none"
                                                style={{ marginRight: '4px' }}
                                            >
                                                <circle
                                                    cx="5.16064"
                                                    cy="6.00244"
                                                    r="5.16064"
                                                    fill="#7347B2"
                                                />
                                            </svg>
                                            {activeCardIndexText}
                                        </Typography>
                                    </Grid>
                                )}
                                <Typography className={classes.sectionHeaders}>
                                    1. Choose a type:
                                </Typography>
                                <CardTypeSelector
                                    options={DELIVERY_METHOD_OPTIONS_TWO_LINE}
                                    selectedOption={deliveryMethod}
                                    handleSelection={(deliveryMethod) => {
                                        setDeliveryMethod(deliveryMethod)
                                    }}
                                    isFixedWidth={true}
                                />
                                <div ref={predesignsListRef}>
                                    <Typography
                                        className={classes.sectionHeaders}
                                    >
                                        2. Choose gift card design option{/* */}
                                        <span style={{ color: '#FF3A3A' }}>
                                            *
                                        </span>
                                        :
                                    </Typography>
                                    <Typography className={classes.subheader}>
                                        Select from our library or upload an
                                        image
                                    </Typography>
                                    {loadingOpenLoopProducts ||
                                    loadingGiftTemplates ? (
                                        <div
                                            className={
                                                classes.loadingCardDesignsContainer
                                            }
                                            aria-busy="true"
                                            aria-label="Loading designs"
                                        >
                                            {[...Array(6)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className={
                                                        classes.loadingCardDesignsItem
                                                    }
                                                />
                                            ))}
                                        </div>
                                    ) : errorFetchingOpenLoopProducts ||
                                      errorFetchingGiftTemplates ? (
                                        <Alert
                                            severity="error"
                                            className={
                                                classes.errorLoadingDesignsAlert
                                            }
                                        >
                                            There was a problem getting card
                                            designs. Please upload your own
                                            photo or refresh the page to try
                                            again.
                                        </Alert>
                                    ) : (
                                        <>
                                            <BacCardDesignSelector
                                                itemCollection={
                                                    designItemsCollection
                                                }
                                                cloudinaryIdProperty={
                                                    cloudinaryIdProperty
                                                }
                                                imageUrlProperty={
                                                    imageUrlProperty
                                                }
                                                designNameProperty={
                                                    designNameProperty
                                                }
                                                selectedItem={
                                                    selectedCardDesign
                                                }
                                                onItemSelected={onDesignSelect}
                                                overlay={embossOverlay}
                                                displayLimit={4} // Limit to 4 designs
                                                onShowAllItemsSelected={
                                                    handleShowAllDesigns
                                                }
                                                handleImageChange={
                                                    handleImageChange
                                                }
                                                hasSelectedPhoto={
                                                    hasSelectedPhoto
                                                }
                                                setImageRulesCheckResult={
                                                    setImageRulesCheckResult
                                                }
                                                imageInputRef={imageInputRef}
                                                setModalIsOpen={setModalIsOpen}
                                            />
                                            <DesignSelectionModal
                                                isOpen={modalOpen}
                                                onClose={handleCloseModal}
                                                itemCollection={
                                                    designItemsCollection
                                                }
                                                cloudinaryIdProperty={
                                                    cloudinaryIdProperty
                                                }
                                                imageUrlProperty={
                                                    imageUrlProperty
                                                }
                                                designNameProperty={
                                                    designNameProperty
                                                }
                                                selectedItem={
                                                    selectedCardDesign
                                                }
                                                onItemSelected={onDesignSelect}
                                                overlay={embossOverlay}
                                            />
                                        </>
                                    )}
                                    <FormHelperText
                                        ref={imageErrorRef}
                                        error={imageError.length > 0}
                                        className={classes.errorText}
                                    >
                                        {imageError}
                                    </FormHelperText>
                                </div>
                                <CustomCardForm
                                    applyCardImageToAll={applyCardImageToAll}
                                    baseUrl={baseUrl}
                                    cardNetwork={cardNetwork}
                                    cloudinaryId={cloudinaryId}
                                    defaultCardNetwork={defaultCardNetwork}
                                    defaultDeliveryMethod={
                                        defaultDeliveryMethod
                                    }
                                    deliveryMethod={deliveryMethod}
                                    isVirtual={isVirtual}
                                    messageOnCard={messageOnCard}
                                    nameOnCard={nameOnCard}
                                    setDeliveryMethod={setDeliveryMethod}
                                    selectedCardDesign={selectedCardDesign}
                                    setImageError={setImageError}
                                    setMessageOnCard={setMessageOnCard}
                                    setNameOnCard={setNameOnCard}
                                    setQuantity={setQuantity}
                                    showApplyAllCheckbox={showApplyAllCheckbox}
                                    quantity={quantity}
                                    amount={amount}
                                    setAmount={setAmount}
                                    imageErrorRef={imageErrorRef}
                                    urlData={urlData}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Container>
            </Container>
        </div>
    )
}

export default CreateACardLanding
